/*  MUI  */
import { Box, TableCell, Typography } from '@mui/material';

/* Shared */
import { DateFormat } from 'application/Shared/Components/Core';
import {
    StyledIncidentImpact,
    StyledIncidentStatus
} from 'application/Shared/Components/Incidents';
import { ManagedTable } from 'application/Shared/Components/Table';
import { INCIDENT_STATUS } from 'application/Shared/Constants';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const IncidentImpactInfo = ({ incidentImpact, isResolved = false }) => {
    const { t } = useTranslation();
    return (
        <Box>
            <Typography color="textSecondary" fontWeight={600}>
                {t('Common.impact')}
            </Typography>
            <StyledIncidentImpact
                incidentImpact={incidentImpact?.toLowerCase()}
                isResolved={isResolved}
            />
        </Box>
    );
};

const IncidentStatusInfo = ({ incidentStatus, isResolved = false }) => {
    const { t } = useTranslation();
    return (
        <Box>
            <Typography color="textSecondary" fontWeight={600}>
                {t('Common.status')}
            </Typography>
            <StyledIncidentStatus
                incidentStatus={incidentStatus?.toLowerCase()}
                isResolved={isResolved}
            />
        </Box>
    );
};

const TableRows = ({ row }) => {
    const { t } = useTranslation();

    const isResolved =
        row.incidentStatus.toLowerCase() === INCIDENT_STATUS.RESOLVED;

    return (
        <TableCell>
            <Box style={{ minHeight: '185px' }}>
                <Box display="flex" justifyContent="space-between" mb="16px">
                    <IncidentImpactInfo
                        incidentImpact={row.incidentImpact?.toLowerCase()}
                        isResolved={isResolved}
                    />
                    <IncidentStatusInfo
                        incidentStatus={row.incidentStatus?.toLowerCase()}
                        isResolved={isResolved}
                    />
                </Box>
                <Box mb="16px">
                    <Typography color="textSecondary" fontWeight={600}>
                        {t('Common.lastIncidentUpdate')}
                    </Typography>
                    <DateFormat date={row.lastIncidentUpdate} noTz />
                </Box>
                <Box>
                    <Typography color="textSecondary" fontWeight={600}>
                        {t('Common.description')}
                    </Typography>
                    {row.lastIncidentUpdateDescription}
                </Box>
            </Box>
        </TableCell>
    );
};

const tableConstants = Object.freeze({
    renderBodyFn: (row) => <TableRows row={row} />,
    searchFields: [
        'name',
        'incidentImpact',
        'incidentStatus',
        'lastIncidentUpdateDescription'
    ]
});

export const AppStatusDetails = ({ applicationId }) => {
    const { t } = useTranslation();

    const memoized = useMemo(
        () => ({
            headers: [
                {
                    align: 'left',
                    field: 'lastIncidentUpdateDescription',
                    renderHeader: <strong>{t('Common.entries')}</strong>,
                    width: '100%'
                }
            ],
            noResultsRender: (
                <Box mt="4px">
                    <Typography variant="p">
                        {t('Applications.incidentsTable.noIncidents')}
                    </Typography>
                </Box>
            ),
            parameters: { applicationId }
        }),
        [applicationId, t]
    );

    return (
        <Box>
            <Typography
                color="textSecondary"
                sx={{ fontWeight: 600, marginBottom: '8px' }}
            >
                {t('Applications.incidentsTable.title')}
            </Typography>
            <ManagedTable
                headers={memoized.headers}
                noResultsRender={memoized.noResultsRender}
                parameters={memoized.parameters}
                renderBody={tableConstants.renderBodyFn}
                renderBodyKey="id"
                renderFooter
                renderHeaderRow={false}
                rowsApi="/appStatus"
                rowsApiPreload
                rowsPerPage={5}
                searchEnabled={false}
                searchFields={tableConstants.searchFields}
                sortDirection="DESC"
                sortFieldType="string"
                sortKey="lastIncidentUpdateDescription"
            />
        </Box>
    );
};
